import SignatureImage from "../../components/SignatureImage";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Select from "react-select";
import "../../styles/ReferenceCheckFormPage.css";

const CONSENT_TITLE = "Review and Sign Consent - Consent to Previous Employment";

const ReferenceCheckFormPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [questions, setQuestions] = useState([]);
  const [check, setCheck] = useState(null);
  const [consent, setConsent] = useState(null);
  const [applicantName, setApplicantName] = useState("");
  const [view, setView] = useState("intro");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 48,
      borderRadius: 12,
      backgroundColor: "#ffffff",
      borderColor: state.isFocused ? "#147a7e" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #147a7e" : "none",
      "&:hover": {
        borderColor: "#147a7e",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#ffffff",
      borderRadius: 12,
      overflow: "hidden",
      zIndex: 20,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#147a7e"
        : state.isFocused
          ? "#d7efef"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#1f2937",
      cursor: "pointer",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#d7efef",
      borderRadius: 999,
      paddingInline: 4,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#0f5d60",
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#0f5d60",
      ":hover": {
        backgroundColor: "#147a7e",
        color: "#ffffff",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6b7280",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1f2937",
    }),
  };

  const parseOptions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${BASE_URL}/api/reference-check/public/${token}`
        );
        setQuestions(res.data.questions || []);
        setCheck(res.data.check || null);
        setConsent(res.data.consent || null);
        setApplicantName(res.data.applicant_name || "");
        if (res.data.already_completed) {
          setSuccess(
            res.data.message || "Thank you. The reference check has been completed."
          );
        }
      } catch (err) {
        console.error("Failed to load reference form:", err);
        setError(
          err.response?.data?.message || "Unable to load reference check."
        );
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [BASE_URL, token]);

  const currentQuestion = questions[step];
  const progress = useMemo(() => {
    if (!questions.length) return 0;
    return Math.round(((step + 1) / questions.length) * 100);
  }, [questions.length, step]);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const currentValue = currentQuestion
    ? answers[currentQuestion.rcq_id] ??
      (currentQuestion.rcq_type === "multiselect" ? [] : "")
    : "";

  const validateCurrentStep = () => {
    if (!currentQuestion) return true;
    if (!currentQuestion.rcq_required) return true;
    if (Array.isArray(currentValue)) return currentValue.length > 0;
    return String(currentValue || "").trim().length > 0;
  };

  const goNext = () => {
    if (!validateCurrentStep()) {
      setError("Please answer this question before continuing.");
      return;
    }
    setError("");
    setStep((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const goBack = () => {
    setError("");
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const startReferenceCheck = () => {
    setError("");
    setView("consent");
  };

  const continueFromConsent = () => {
    if (!questions.length) {
      setError("No reference check questions are currently available.");
      return;
    }
    setError("");
    setStep(0);
    setView("questions");
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      setError("Please answer this question before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await axios.post(`${BASE_URL}/api/reference-check/public/${token}/submit`, {
        answers: questions.map((question) => ({
          rcq_id: question.rcq_id,
          answer:
            answers[question.rcq_id] ??
            (question.rcq_type === "multiselect" ? [] : ""),
        })),
      });
      setSuccess("Thank you. The reference check has been completed.");
    } catch (err) {
      console.error("Failed to submit reference form:", err);
      setError(
        err.response?.data?.message || "Unable to submit reference check."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.rcq_type) {
      case "text":
        return (
          <input
            className="form-control reference-check-answer-box"
            value={currentValue}
            onChange={(e) =>
              updateAnswer(currentQuestion.rcq_id, e.target.value)
            }
          />
        );

      case "number":
        return (
          <input
            type="number"
            className="form-control reference-check-answer-box"
            value={currentValue}
            onChange={(e) =>
              updateAnswer(currentQuestion.rcq_id, e.target.value)
            }
          />
        );

      case "date":
        return (
          <input
            type="date"
            className="form-control reference-check-answer-box"
            value={currentValue}
            onChange={(e) =>
              updateAnswer(currentQuestion.rcq_id, e.target.value)
            }
          />
        );

      case "boolean":
        return (
          <div className="reference-check-options">
            {["Yes", "No"].map((option) => (
              <label key={option} className="reference-check-option">
                <input
                  type="radio"
                  name={`q-${currentQuestion.rcq_id}`}
                  checked={currentValue === option}
                  onChange={() =>
                    updateAnswer(currentQuestion.rcq_id, option)
                  }
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case "radio":
      case "select":
        return (
          <Select
            styles={selectStyles}
            options={parseOptions(currentQuestion.rcq_options).map((option) => ({
              value: option,
              label: option,
            }))}
            value={currentValue ? { value: currentValue, label: currentValue } : null}
            onChange={(option) =>
              updateAnswer(currentQuestion.rcq_id, option?.value || "")
            }
          />
        );

      case "multiselect":
        return (
          <Select
            isMulti
            styles={selectStyles}
            options={parseOptions(currentQuestion.rcq_options).map((option) => ({
              value: option,
              label: option,
            }))}
            value={(Array.isArray(currentValue) ? currentValue : []).map(
              (value) => ({
                value,
                label: value,
              })
            )}
            onChange={(selected) =>
              updateAnswer(
                currentQuestion.rcq_id,
                (selected || []).map((option) => option.value)
              )
            }
          />
        );

      case "textarea":
      default:
        return (
          <textarea
            className="form-control reference-check-answer-box"
            rows={7}
            value={currentValue}
            onChange={(e) =>
              updateAnswer(currentQuestion.rcq_id, e.target.value)
            }
          />
        );
    }
  };

  if (loading) {
    return <div className="reference-check-public-shell">Loading...</div>;
  }

  if (error && !questions.length) {
    return <div className="reference-check-public-shell">{error}</div>;
  }

  if (success) {
    return (
      <div className="reference-check-public-shell">
        <div className="reference-check-public-card">
          <h1>Reference Check Completed</h1>
          <p>{success}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reference-check-public-shell">
      <div className="reference-check-public-card">
        <div className="reference-check-public-header">
          <div>
            <div className="reference-check-kicker">Reference Check</div>
            <h1>{applicantName || "Applicant"} Reference Check</h1>
            <p>
              {check?.wh_name || "Previous employer"} is being asked to confirm
              employment details, review the signed consent, and answer a few
              questions.
            </p>
          </div>
          <div className="reference-check-meta">
            <div>Position: {check?.wh_position || "-"}</div>
            <div>
              Employment: {formatDate(check?.wh_datefrom)} to{" "}
              {check?.wh_dateto ? formatDate(check.wh_dateto) : "Present"}
            </div>
          </div>
        </div>

        {view === "intro" && (
          <div className="reference-check-stage-card">
            <div className="reference-check-stage-label">Step 1 of 3</div>
            <h2>Before you begin</h2>
            <p>
              You are completing a reference check for{" "}
              <strong>{applicantName || "this applicant"}</strong> regarding
              their employment with{" "}
              <strong>{check?.wh_name || "this previous employer"}</strong>.
            </p>
            <div className="reference-check-info-grid">
              <div className="reference-check-info-tile">
                <span>Sent From</span>
                <strong>New Way Trucking</strong>
              </div>
              <div className="reference-check-info-tile">
                <span>Applicant</span>
                <strong>{applicantName || "-"}</strong>
              </div>
              <div className="reference-check-info-tile">
                <span>Previous Employer</span>
                <strong>{check?.wh_name || "-"}</strong>
              </div>
              <div className="reference-check-info-tile">
                <span>Reference Contact</span>
                <strong>
                  {`${check?.wh_confname || ""} ${check?.wh_conlname || ""}`.trim() ||
                    "-"}
                </strong>
              </div>
            </div>
            <div className="reference-check-actions reference-check-actions-intro">
              <div />
              <button className="btn btn-primary" onClick={startReferenceCheck}>
                Start
              </button>
            </div>
          </div>
        )}

        {view === "consent" && (
          <div className="reference-check-stage-card reference-check-consent-card">
            <div className="reference-check-stage-label">Step 2 of 3</div>
            <h2>{CONSENT_TITLE}</h2>
            <p>
              This applicant already signed the consent below to authorize{" "}
              {check?.wh_name || "the previous employer"} to release prior
              employment information.
            </p>

            <div className="reference-check-consent-copy">
              <p>
                I hereby authorize my previous employers to release and forward
                all information regarding my alcohol and controlled substances
                testing and any violations of Department of Transportation
                (DOT) regulations within the previous three years from the date
                below. This information will be used for the purpose of
                investigating my safety performance history as required by 49
                CFR 391.23 of the Federal Motor Carrier Safety Regulations.
              </p>
              <p>
                I understand that the information released will be used to
                determine my eligibility for employment as a driver. I release
                my previous employers from any liability for providing such
                information.
              </p>
            </div>

            <div className="reference-check-consent-employer">
              This document applies to the following previous employer:{" "}
              <strong>{consent?.employer_name || check?.wh_name || "-"}</strong>
            </div>

            <div className="reference-check-consent-fields">
              <div className="reference-check-consent-field">
                <span>Employee Name</span>
                <strong>{consent?.employee_name || applicantName || "-"}</strong>
              </div>
              <div className="reference-check-consent-field">
                <span>Date</span>
                <strong>{formatDate(consent?.signed_at)}</strong>
              </div>
            </div>

            <div className="reference-check-consent-signature">
              <span>Signature</span>
              {consent?.signature ? (
                <div className="reference-check-signature-wrap">
                  <SignatureImage
                    src={consent.signature}
                    alt="Applicant signature"
                    className="reference-check-signature-image"
                    draggable="false"
                  />
                </div>
              ) : (
                <div className="reference-check-signature-missing">
                  Signature not available.
                </div>
              )}
            </div>

            <div className="reference-check-actions reference-check-actions-consent">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setView("intro")}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={continueFromConsent}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {view === "questions" && (
          <>
            <div className="reference-check-progress-wrap">
              <div className="reference-check-progress-bar">
                <div
                  className="reference-check-progress-value"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="reference-check-progress-text">
                Question {step + 1} of {questions.length}
              </div>
            </div>

            {currentQuestion && (
              <div className="reference-check-question-card">
                <h2>{currentQuestion.rcq_question}</h2>
                {currentQuestion.rcq_description && (
                  <p>{currentQuestion.rcq_description}</p>
                )}

                {renderInput()}
              </div>
            )}
          </>
        )}

        {error && <div className="text-danger mb-3">{error}</div>}

        {view === "questions" && (
          <div className="reference-check-actions">
            <button
              className="btn btn-outline-secondary"
              onClick={step === 0 ? () => setView("consent") : goBack}
              disabled={submitting}
            >
              Back
            </button>
            {step < questions.length - 1 ? (
              <button className="btn btn-primary" onClick={goNext}>
                Next
              </button>
            ) : (
              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Reference Check"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceCheckFormPage;
